import { useAuth0 } from '@auth0/auth0-react';
import {
    Box,
    Grid,
    styled,
    Typography,
    Paper,
    Avatar,
    Hidden,
    Button,
    Chip,
    Input,
    TextField,
    Divider,
    LinearProgress,
    linearProgressClasses,
    CircularProgress,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
    FormControlLabel,
} from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import axios from 'axios';
import { intervalToDuration, parse } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import splitCamelCaseToString from '../../utils/titleCase';

const fileVerifier = type => {
    return !!(
        type === 'image/jpeg' ||
        type === 'image/png' ||
        type === 'image/jpg' ||
        type === 'image/webp' ||
        type === 'application/pdf'
    );
};

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    margin: '8px',
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.primary.light,
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.primary.main,
    },
}));

const GeneralText = styled(TextField)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const StyledBox = styled(Box)(({ theme }) => ({
    padding: '0px 14px 14px 14px',
    height: '80%',
    textAlign: 'center',
    position: 'relative',
}));

const BookingButton = styled(Button)(({ theme }) => ({
    padding: '10px 0px',
    margin: '10px auto',
    width: '100%',
    maxWidth: '150px',
    borderRadius: '30px',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: '1rem',
    margin: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover, &:focus': {
        boxShadow:
            '0px 7px 8px -4px rgba(0,0,0,0.2), 0px 12px 17px 2px rgba(0,0,0,0.14), 0px 5px 22px 4px rgba(0,0,0,0.12)',
    },
    [theme.breakpoints.down('sm')]: {
        margin: '2rem .3rem',
    },
    [theme.breakpoints.up('sm')]: {
        maxWidth: '45rem',
        margin: '2rem auto',
    },
}));

const ApplyForJob = props => {
    const coverLetterLocation = useRef(null);

    // let history = useHistory();
    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
    const [state, setState] = useState({
        coverLetter: {
            value: '',
            touched: false,
            error: false,
            errorCode: '',
        },
        uploadingState: '',
    });
    const [documents, setDocuments] = useState([]);
    const [myDocuments, setMyDocuments] = useState({
        status: 0,
        documents: [],
    });
    const [progress, setProgress] = useState({ loaded: 0, total: 100 });
    const [documentError, setDocumentError] = useState('No Document Uploaded');

    const profile = useSelector(state => state.profile.profile);
    const profileStatus = useSelector(state => state.profile.status);
    const [selectDocumentValue, setSelectDocumentValue] =
        React.useState('female');

    const handleSelectDocument = event => {
        setSelectDocumentValue(event.target.value);
    };

    useEffect(() => {
        const getMyDocuments = async () => {
            const token = await getAccessTokenSilently({
                scope: process.env.REACT_APP_scope,
            });
            try {
                const profile = await axios.get('/getMyDocuments', {
                    baseURL: process.env.REACT_APP_API_GATEWAY,
                    timeout: 30000,
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                });
                console.log(profile);
                setMyDocuments({
                    status: 1,
                    documents: profile.data.Message.Data.filter(doc => {
                        return doc.sk1.substring(0, 6) === 'Resume';
                    }),
                });
            } catch (err) {
                console.log(err);
                setMyDocuments({ status: 2, documents: [] });
            }
        };
        getMyDocuments();
    }, []);

    const updateField = event => {
        const { id, value } = event.target;
        var itemStruct = {
            value: value,
            touched: true,
            error: state.error,
            errorCode: state.errorCode,
        };
        setState({
            ...state,
            [id]: itemStruct,
        });
    };

    const exitField = event => {
        const { id, value } = event.target;
        // save field values
        const doNothing = value;
        console.log(doNothing);
        var itemStruct = {
            value: state[id].value,
            touched: true,
            error: false,
            errorCode: '',
        };
        if (
            state[id].value.match(/\w/) === null ||
            state[id].value.match(/[<>]/)
        ) {
            itemStruct.error = true;
            itemStruct.errorCode = 'Please input your ' + id;
        }
        console.log(event.target);
        setState({
            ...state,
            [id]: itemStruct,
        });
    };

    const fileSelectHandler = event => {
        console.log(event.target.files);
        for (const document of event.target.files) {
            if (fileVerifier(document.type)) {
                console.log(document.name, ' is a supported type');
                setDocuments(event.target.files);
            } else {
                setDocumentError(
                    'Unsupported File Type. Upload PDFs or Images'
                );
            }
        }
    };

    const handleSubmission = async () => {
        if (
            ((documents.length > 0 && selectDocumentValue === 'Upload') ||
                selectDocumentValue !== 'Upload') &&
            state.coverLetter !== ''
        ) {
            const token = await getAccessTokenSilently({
                scope: process.env.REACT_APP_scope,
            });
            setState({
                ...state,
                uploadingState: 'Writing text to booking...',
            });

            try {
                setProgress({
                    loaded: 5,
                    total: 100,
                });
                let contentType =
                    documents.length > 0
                        ? documents[0].type
                        : myDocuments.documents.filter(
                              doc => doc.url === selectDocumentValue
                          )[0].filetype;

                let document = {
                    filename:
                        documents.length > 0
                            ? documents[0].name
                            : selectDocumentValue,
                    size:
                        documents.length > 0
                            ? documents[0].size
                            : myDocuments.documents.filter(
                                  doc => doc.url === selectDocumentValue
                              )[0].size,
                    filetype: contentType,
                    type: 'JobResume',
                    description: `Job Application for ${props.jobTitle}`,
                    viewable: true,
                    data: {
                        coverLetter: state.coverLetter.value,
                    },
                    schoolId: props.jobId,
                    reuseFilename:
                        selectDocumentValue === 'Upload' ? false : true,
                };

                const permission = await axios.post(
                    '/uploadDocumentPermissionsAdvanced',
                    document,
                    {
                        baseURL: process.env.REACT_APP_API_GATEWAY,
                        timeout: 30000,
                        headers: {
                            authorization: `Bearer ${token}`,
                            'Content-Type': contentType,
                        },
                    }
                );
                setState({
                    ...state,
                    uploadingState: 'Uploading Document...',
                });
                console.log(JSON.stringify(permission));
                if (
                    documents.length > 0 &&
                    selectDocumentValue === 'Upload' &&
                    typeof permission.data.uploadURL !== 'undefined'
                ) {
                    axios
                        .put(permission.data.uploadURL, documents[0], {
                            timeout: 1000000,
                            headers: {
                                'Content-Type': documents[0].type,
                            },
                            onUploadProgress: function (progressEvent) {
                                setProgress({
                                    loaded: progressEvent.loaded,
                                    total: progressEvent.total,
                                });
                            },
                        })
                        .then(() => {
                            setState({
                                ...state,
                                uploadingState: 'Completed',
                            });
                            props.applied();
                        })
                        .catch(err => {
                            console.log(err);
                            setState({
                                ...state,
                                uploadingState: 'Failed',
                            });
                            setProgress({
                                loaded: 0,
                                total: 100,
                            });
                        });
                    //TODO: Record the submission on user profile
                } else {
                    setProgress({
                        loaded: 100,
                        total: 100,
                    });
                    props.applied();
                    //TODO: Record the submission on user profile
                }
            } catch (err) {
                console.log(err);
                console.log(err.response.data);
                if (err.response !== undefined) {
                    setState({
                        ...state,
                        uploadingState: 'Failed. ' + err.response.data,
                    });
                } else {
                    setState({
                        ...state,
                        uploadingState: 'Failed',
                    });
                }

                setProgress({
                    loaded: 0,
                    total: 100,
                });
            }
        } else {
            setState({
                ...state,
                error: true,
                errorText:
                    'Please fill in fields before submitting an application',
            });
        }
    };

    return (
        <Box flexDirection="row">
            <Divider></Divider>
            <Typography
                sx={{ margin: '12px 0px 12px 0px' }}
                variant="h5"
            >{`Submit application for ${splitCamelCaseToString(
                profile.Data.Items[0].firstName
            )} ${splitCamelCaseToString(
                profile.Data.Items[0].lastName
            )}`}</Typography>
            <Typography variant="body">Details</Typography>
            <Typography variant="body2">
                {profile.Data.Items[0].phoneNumber}
            </Typography>
            <Typography variant="body2">
                {profile.Data.Items[0].email}
            </Typography>
            <GeneralText
                ref={coverLetterLocation}
                error={state.coverLetter.error}
                id="coverLetter"
                data-cy="cover-letter"
                label="Cover Letter"
                variant="outlined"
                helperText={state.coverLetter.errorCode}
                value={state.coverLetter.value}
                onChange={updateField}
                onBlur={exitField}
                rows={4}
                fullWidth
                multiline
            ></GeneralText>

            {myDocuments.status === 1 ? (
                <Box>
                    <FormControl>
                        <FormLabel id="radio-buttons-file-select">
                            Existing Resume
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="select-resume"
                            defaultValue="Upload"
                            name="radio-buttons-resume-select-group"
                            value={selectDocumentValue}
                            onChange={handleSelectDocument}
                        >
                            {myDocuments.documents.map(document => {
                                return (
                                    <FormControlLabel
                                        value={document.url}
                                        control={<Radio />}
                                        label={document.url}
                                    />
                                );
                            })}
                            <FormControlLabel
                                value="Upload"
                                control={<Radio />}
                                label="Upload"
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>
            ) : (
                <>
                    {myDocuments.status === 0 ? (
                        <CircularProgress />
                    ) : (
                        <>
                            <Typography>Failed to load documents</Typography>
                        </>
                    )}
                </>
            )}
            {(myDocuments.documents.length === 0 ||
                selectDocumentValue === 'Upload') && (
                <Box flexDirection="row">
                    {documents.length > 0 ? (
                        <Box>
                            <Typography>
                                {`${documents[0].name} • ${(
                                    documents[0].size / 1000000
                                ).toFixed(2)} MB`}
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="body2">
                                {documentError}
                            </Typography>
                        </Box>
                    )}
                    <BookingButton
                        variant="contained"
                        component="label"
                        sx={{ m: 1 }}
                        disabled={progress.loaded > 0}
                    >
                        Upload File
                        <input
                            // className={classes.input}
                            id="contained-button-file"
                            data-cy="upload-file"
                            type="file"
                            onChange={fileSelectHandler}
                            hidden
                        />
                    </BookingButton>
                </Box>
            )}

            {progress.loaded === 0 ? (
                <>
                    <BookingButton
                        variant="contained"
                        onClick={handleSubmission}
                        data-cy="submit"
                        sx={{ m: 1 }}
                        disabled={progress.loaded > 0}
                    >
                        Submit
                    </BookingButton>
                    <BookingButton
                        onClick={props.closeApply}
                        disabled={progress.loaded > 0}
                    >
                        Cancel
                    </BookingButton>
                </>
            ) : (
                <>
                    <BookingButton onClick={props.closeApply}>
                        Close
                    </BookingButton>
                </>
            )}

            <Typography variant="body2">{state.uploadingState}</Typography>
            <BorderLinearProgress
                variant="determinate"
                value={(progress.loaded / progress.total) * 100}
            />
        </Box>
    );
};

export default ApplyForJob;
